import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { useResizeDetector } from 'react-resize-detector'
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
// import MassiveModificationModal from '../../components/massiveModificationModal/MassiveModificationModal'
import { useTranslation } from 'react-i18next'
import {
  DayDataList,
  DayDataTypes
} from '../../../../api/models/dataList.model'
import RatesUtilityBar from '../../components/RatesUtilityBar'
import StickyBar from '../../components/StickyBar'
import AvailabilityLegendModal from './components/AvailabilityLegendModal'
import useModal from 'src/hooks/useModal'
import RatesDataGridContextProvider, {
  useRatesDataGridContext
} from './contexts/ratesDataGrid/RatesDataGridContext'
import FilterBar from './components/FilterBar'
import { ConfirmModal, CustomBackdrop } from 'src/components'
import DayListBar from './components/DayListBar'
import VirtualizedGrid from './components/VirtualizedGrid'
import RatesGridCol1CellRender from './components/RatesGridCol1CellRender'
import RatesGridCol2CellRender from './components/RatesGridCol2CellRender'
import { dateUtils } from 'src/utils/date.util'
import RatesGridEditCellRender from './components/ratesGridEditCellRender/RatesGridEditCellRender'

import RefreshIcon from '@mui/icons-material/Refresh'
import MassiveModificationModal from '../../components/massiveModificationModal/MassiveModificationModal'
import {
  CapacityRow,
  ChannelRow,
  RateRow,
  RatesDataGridRow,
  RowType
} from './contexts/ratesDataGrid/RatesDataGridContext.types'
import { useDesktopWindowContext } from 'src/pages/desktop/components/desktopWindow/DesktopWindowRenderer'

export type RatesRowType =
  | 'rate'
  | 'channel'
  | 'hotel'
  | 'roomType'
  | 'channelSkeleton'
  | 'rateSkeleton'
export interface RatesRowData {
  id: string
  filtered?: boolean
  fatherId?: string
  type: RatesRowType
  show?: boolean
  haveChildren?: boolean
  inherited?: boolean
  editable?: boolean
  dataType?: keyof DayDataTypes
  dayData?: DayDataList[]
  hotelId?: string
  roomTypeId?: string
  rateId?: string
  numberChannelList?: number
  numberCapacityList?: number
  roomTypeCapacityList?: string[]
  roomTypeCapacityId?: string
  inheritedDataList?: string[]
  [key: string]: any
}

export interface ChangedRatesRowData {
  dataType: keyof DayDataTypes
  value: any
  day: string
  row: RatesRowData
}

// const getInBetweenDates = (dates?: ApiRateWindowStateDateRange) => {
//   if (dates == null) return []
//   const newDates = []
//   let currentDate = moment(dates.iniDate, 'DD-MM-YYYY')
//   const endDate = moment(dates.endDate, 'DD-MM-YYYY')

//   while (!currentDate.isAfter(endDate)) {
//     newDates.push(currentDate.clone())
//     currentDate = currentDate.add(1, 'day')
//   }

//   return newDates
// }

const RatesDataGridComponent: ReactFC = React.memo(() => {
  const { windowId } = useDesktopWindowContext()
  const {
    loading: ratesContextLoading,
    apiData,
    refreshData,
    rows,
    daysList,
    editedDaysList,
    saveEditedData,
    editedDaysListOverLimit
  } = useRatesDataGridContext()

  const gridApiRef = useGridApiRef()
  const scrollableEl = useRef<HTMLDivElement>(null)
  const { ref: containerEl } = useResizeDetector()
  const { width: leftColumnWidth, ref: leftColumnEl } = useResizeDetector()
  const { width: rightColumnWidth, ref: rightColumnEl } = useResizeDetector()
  const { t } = useTranslation()
  const dataGridContainerEl = useRef<HTMLDivElement>()
  const prevTableScrollableElScrollPos = useRef(0)
  const prevScrollableElScrollPos = useRef(0)

  const [isOverLimitModalOpen, setIsOverLimitModalOpen] = useState(false)

  // const tableScrollableEl = useRef<HTMLDivElement>()
  const [massiveModificationModalOpen, setMassiveModificationModalOpen] =
    React.useState<{
      open: boolean
      row?: RateRow | CapacityRow | ChannelRow
    }>({
      open: false,
      row: undefined
    })
  const {
    isModalOpen: isAvailabilityLegendModalOpen,
    toggleModal: toggleAvailabilityLegendModal
  } = useModal()

  const initialLoad = ratesContextLoading && !apiData

  const cellWidth = (rightColumnWidth ?? 0) / 14

  const handleRefresh = async () => {
    await refreshData()
  }

  useEffect(() => {
    const tableEl = document
      .querySelector(`.table-${windowId}`)
      ?.querySelector(`.MuiDataGrid-virtualScroller`) as HTMLDivElement

    const currentScrollableEl = scrollableEl.current

    const syncScrolls = (
      scrollLength: number,
      ElToSync: HTMLDivElement | undefined
    ) => {
      if (ElToSync != null) ElToSync.scrollLeft = scrollLength
    }
    const handleTableScrollableElScroll = (e: Event) => {
      const scrollLength = (e.currentTarget as HTMLDivElement).scrollLeft
      if (prevTableScrollableElScrollPos.current === scrollLength) return
      currentScrollableEl?.removeEventListener(
        'scroll',
        handleScrollableElScroll
      )
      prevTableScrollableElScrollPos.current = scrollLength
      syncScrolls(scrollLength, currentScrollableEl!)
      currentScrollableEl?.addEventListener('scroll', handleScrollableElScroll)
    }
    const handleScrollableElScroll = (e: Event) => {
      const scrollLength = (e.currentTarget as HTMLDivElement).scrollLeft
      if (prevScrollableElScrollPos.current === scrollLength) return
      tableEl?.removeEventListener('scroll', handleTableScrollableElScroll)
      prevScrollableElScrollPos.current = scrollLength
      syncScrolls(scrollLength, tableEl!)
      tableEl?.addEventListener('scroll', handleTableScrollableElScroll)
    }

    tableEl?.addEventListener('scroll', handleTableScrollableElScroll)
    currentScrollableEl?.addEventListener('scroll', handleScrollableElScroll)

    return () => {
      tableEl?.removeEventListener('scroll', handleTableScrollableElScroll)
      currentScrollableEl?.removeEventListener(
        'scroll',
        handleScrollableElScroll
      )
    }
  }, [scrollableEl, windowId, daysList])

  const saveAndRefresh = async () => {
    await saveEditedData()
    await refreshData()
  }

  const handleSave = () => {
    if (editedDaysListOverLimit.length > 0) {
      openOverLimitConfirmModal()
    } else {
      saveAndRefresh()
    }
  }

  const handleConfirmSave = async () => {
    closeOverLimitConfirmModal()
    saveAndRefresh()
  }

  const handleCancelConfirm = () => {
    closeOverLimitConfirmModal()
  }

  const openOverLimitConfirmModal = () => {
    setIsOverLimitModalOpen(true)
  }

  const closeOverLimitConfirmModal = () => {
    setIsOverLimitModalOpen(false)
  }

  const handleOpenCreateRatePriceModal = (
    row: RateRow | CapacityRow | ChannelRow
  ) => {
    setMassiveModificationModalOpen({
      open: true,
      row
    })
  }
  const handleOpenMassiveModificationModalOpen = () =>
    setMassiveModificationModalOpen({
      open: true
    })
  const handleCloseMassiveModificationModalOpen = () =>
    setMassiveModificationModalOpen({
      open: false
    })

  const haveChangedCells = Object.keys(editedDaysList).length > 0

  const columns: GridColDef[] = useMemo(
    () => [
      {
        width: (leftColumnWidth ?? 0) * 0.68,
        field: 'col1',
        headerName: 'Tipo 1 (Unidades totales 5)',
        resizable: false,
        editable: false,
        sortable: false,
        headerClassName: 'Avail-datagrid-header',
        renderCell: (params) => {
          return <RatesGridCol1CellRender cellParams={params} />
        }
      },
      {
        width: (leftColumnWidth ?? 0) * 0.32,
        field: 'col2',
        headerName: '',
        resizable: false,
        sortable: false,
        headerClassName: 'Avail-datagrid-header',
        cellClassName: () => 'ratesDataGridCol2',
        renderCell: (params) => {
          return (
            <RatesGridCol2CellRender
              cellParams={params}
              openCreateRatePriceModal={handleOpenCreateRatePriceModal}
              restrictionTypeList={apiData?.restrictionTypeList ?? []}
            />
          )
        }
      },
      ...daysList.map(
        (columnDate, columnIndex): GridColDef => ({
          width: cellWidth,
          field: dateUtils.formatDateToString(columnDate),
          headerName: dateUtils.toDayString(columnDate),
          resizable: false,
          align: 'center',
          sortable: false,
          disableReorder: true,
          headerAlign: 'center',
          headerClassName: 'Avail-datagrid-header Avail-datagrid-header-bold',
          editable: false,
          cellClassName: (params) => {
            const row = params.row as RatesDataGridRow & { index: number }
            let customClassName = `cellData ${
              row.id
            } ratesDataGrid-row-${row.type.toLowerCase()}`
            const dayOfWeek = dateUtils.dayOfWeek(columnDate)
            const isFridayOrSaturday = dayOfWeek === 4 || dayOfWeek === 5
            if (
              isFridayOrSaturday &&
              (row.type === RowType.RATE ||
                row.type === RowType.CHANNEL ||
                row.type === RowType.CAPACITY ||
                row.type === RowType.CAPACITY_SKELETON ||
                row.type === RowType.CHANNEL_SKELETON)
            ) {
              customClassName += ' weekendCell'
            }
            if (
              'editable' in row &&
              !row.editable &&
              (row.type === RowType.CHANNEL ||
                row.type === RowType.RATE ||
                row.type === RowType.CAPACITY)
            ) {
              customClassName += ' notEditableCell'
            }

            if (
              editedDaysList[row.id] &&
              Object.hasOwn(
                editedDaysList[row.id],
                dateUtils.formatDateToString(columnDate)
              )
            ) {
              customClassName += ' editedCell'
            }
            return customClassName
          },
          renderCell: (params) => {
            return (
              <RatesGridEditCellRender
                params={params}
                gridApiRef={gridApiRef}
                restrictionEditableByChannelList={
                  apiData?.restrictionEditableByChannelList ?? []
                }
                openLegendModal={toggleAvailabilityLegendModal}
                columnIndex={columnIndex}
                dayDate={columnDate}
              />
            )
          }
        })
      )
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridApiRef, leftColumnWidth, cellWidth, apiData, editedDaysList]
  )

  const rowsWithIndex = useMemo(
    () => rows.map((row, index) => ({ ...row, index })),
    [rows]
  )

  return (
    <Box
      sx={{
        position: 'relative'
      }}
      ref={containerEl}
    >
      <StickyBar>
        <RatesUtilityBar>
          <FilterBar
            sx={{
              width: '70%'
            }}
          />
          <Box
            sx={{
              marginLeft: 'auto',
              width: '30%',
              textAlign: 'right',
              '& > *:not(:first-of-type)': {
                marginLeft: '1rem'
              }
            }}
          >
            <IconButton
              onClick={handleRefresh}
              sx={{
                justifySelf: 'right'
              }}
              disabled={ratesContextLoading}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              onClick={() => handleOpenMassiveModificationModalOpen()}
              variant='outlined'
            >
              {t('apps.rates.rateDataGrid.labels.massive_update')}
            </Button>
            <Button
              variant='contained'
              disabled={!haveChangedCells}
              onClick={handleSave}
            >
              {t('apps.rates.rateDataGrid.labels.save')}
            </Button>
          </Box>
        </RatesUtilityBar>
        <DayListBar
          leftColumnEl={leftColumnEl}
          rightColumnEl={rightColumnEl}
          rightColumnWidth={rightColumnWidth}
          scrollableEl={scrollableEl}
        />
      </StickyBar>
      <Box
        ref={dataGridContainerEl}
        sx={{
          position: 'relative',
          marginTop: '10px',
          borderRadius: 2,
          overflow: 'hidden',
          width: {
            xs: '100%',
            xl: `${(leftColumnWidth ?? 1440) + cellWidth * 14}px`
          },
          height: 800,
          '@media screen and (min-height: 750px)': {
            height: '1080px'
          },
          '@media screen and (min-height: 1080px)': {
            height: '1200px'
          }
        }}
      >
        <VirtualizedGrid
          gridApiRef={gridApiRef}
          rows={rowsWithIndex}
          columns={columns}
          containerEl={dataGridContainerEl.current}
        />
      </Box>
      {!initialLoad && (
        <MassiveModificationModal
          open={massiveModificationModalOpen}
          handleClose={handleCloseMassiveModificationModalOpen}
        />
      )}

      <AvailabilityLegendModal
        open={isAvailabilityLegendModalOpen}
        onClose={toggleAvailabilityLegendModal}
      />
      <ConfirmModal
        body={
          <Box>
            <Typography>
              {t('alerts.more_than_limit_day_value_in_fields', {
                value: '10.000€',
                ns: 'tarifas'
              })}
            </Typography>
            {editedDaysListOverLimit?.map(({ date, row }) => {
              const dateString = dateUtils.formatDateToString(date)
              return (
                <Box key={`${row.id}-${Object}`}>
                  <Typography>{`${t('labels.Day')} ${dateString} ${
                    row.data.name
                  }`}</Typography>
                </Box>
              )
            })}
          </Box>
        }
        open={isOverLimitModalOpen}
        onConfirm={handleConfirmSave}
        onClose={handleCancelConfirm}
        loading={ratesContextLoading}
        cancelLabel={t('labels.Keep_editing')}
      />
      {ratesContextLoading && <CustomBackdrop />}
    </Box>
  )
})

const RatesDataGrid = () => {
  return (
    <RatesDataGridContextProvider>
      <RatesDataGridComponent />
    </RatesDataGridContextProvider>
  )
}

export default React.memo(RatesDataGrid)
